import { IconLineLink, IconLineLive } from "@kalodata/kds-design-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { ClipModal } from "@components/ClipModal/ClipModal";
import { ClipTabs, type ClipTabItem } from "@components/ClipTabs/ClipTabs";
import { ReferenceVideoFilePanel } from "@pages/videoRemake/ReferenceVideoFilePanel";
import { ReferenceVideoLinkPanel } from "@pages/videoRemake/ReferenceVideoLinkPanel";
import { ReferenceVideoLibraryPanel, type LibrarySubTab } from "@pages/videoRemake/ReferenceVideoLibraryPanel";
import { useReferenceVideoLibrary } from "@pages/videoRemake/useReferenceVideoLibrary";
import { useReferenceVideoLink } from "@pages/videoRemake/useReferenceVideoLink";
import { IconFolder } from "@icons/index";

export type ReferenceVideoTab = "videoLink" | "videoFile" | "library";

export type ReferenceVideoPickPayload = {
  source: ReferenceVideoTab;
  data: unknown;
};

export interface ReferenceVideoModalProps {
  open: boolean;
  onClose: () => void;
  /** 选中视频后回调，包含来源（link/file/library）及对应数据 */
  onSelect?: (payload: ReferenceVideoPickPayload) => void;
  /** 解析/选中成功后回调 */
  onPickVideoSuccess?: (payload: ReferenceVideoPickPayload) => void;
}

export const ReferenceVideoModal = ({ open, onClose, onSelect, onPickVideoSuccess }: ReferenceVideoModalProps) => {
  const [activeTab, setActiveTab] = useState<ReferenceVideoTab>("library");
  const [librarySubTab, setLibrarySubTab] = useState<LibrarySubTab>("generated");
  const pickedLinkKeyRef = useRef<string | null>(null);
  const {
    input: videoLinkInput,
    setInput: setVideoLinkInput,
    status: videoLinkStatus,
    preview: videoLinkPreview,
    errorMessage: videoLinkError,
    parseLink: parseVideoLink,
    debouncedParse: debouncedParseVideoLink,
    reset: resetVideoLink,
  } = useReferenceVideoLink();
  const library = useReferenceVideoLibrary({
    enabled: open,
    subTab: librarySubTab,
  });

  useEffect(() => {
    if (!open) {
      setActiveTab("library");
      setLibrarySubTab("generated");
      pickedLinkKeyRef.current = null;
      resetVideoLink();
    }
  }, [open, resetVideoLink]);

  const handlePickSuccess = useCallback(
    (payload: ReferenceVideoPickPayload) => {
      onPickVideoSuccess?.(payload);
      onSelect?.(payload);
      onClose();
    },
    [onPickVideoSuccess, onSelect, onClose],
  );

  /** 设计稿：粘贴后自动解析，成功后直接进入 Studio（无 Continue 按钮） */
  useEffect(() => {
    if (!open || videoLinkStatus !== "succeeded" || !videoLinkPreview) return;

    const pickKey = videoLinkPreview.videoId ?? videoLinkPreview.originVideoLink ?? "";
    if (!pickKey || pickedLinkKeyRef.current === pickKey) return;
    pickedLinkKeyRef.current = pickKey;

    handlePickSuccess({ source: "videoLink", data: videoLinkPreview });
  }, [open, videoLinkStatus, videoLinkPreview, handlePickSuccess]);

  const items: ClipTabItem<ReferenceVideoTab>[] = [
    {
      value: "library",
      label: "Library",
      icon: () => <IconFolder className="w-[20px] h-[20px] shrink-0" />,
      children: (
        <ReferenceVideoLibraryPanel
          subTab={librarySubTab}
          onSubTabChange={setLibrarySubTab}
          videos={library.videos}
          loading={library.loading}
          loadingMore={library.loadingMore}
          hasMore={library.hasMore}
          error={library.error}
          scrollTop={library.scrollTop}
          sentinelRef={library.sentinelRef}
          onScrollTopChange={library.saveScrollTop}
          onRetry={library.refresh}
          onSelect={(video) => handlePickSuccess({ source: "library", data: video })}
        />
      ),
    },
    {
      value: "videoLink",
      label: "Video link",
      icon: () => <IconLineLink size={20} />,
      children: (
        <ReferenceVideoLinkPanel
          value={videoLinkInput}
          onChange={setVideoLinkInput}
          status={videoLinkStatus}
          errorMessage={videoLinkError}
          onDebouncedParse={debouncedParseVideoLink}
          onParseNow={parseVideoLink}
        />
      ),
    },
    {
      value: "videoFile",
      label: "Upload",
      icon: () => <IconLineLive size={20} />,
      children: (
        <ReferenceVideoFilePanel onUploadSuccess={(file) => handlePickSuccess({ source: "videoFile", data: file })} />
      ),
    },
  ];

  return (
    <ClipModal
      open={open}
      onCancel={onClose}
      width={720}
      centered
      mask={{ closable: true }}
      destroyOnHidden
      rootClassName="ReferenceVideoModal"
      data-testid="reference-video-modal"
      styles={{
        body: { padding: 0 },
      }}
      title={t("Reference Video")}
    >
      <ClipTabs<ReferenceVideoTab> value={activeTab} items={items} onChange={setActiveTab} tabListClassName="px-0" />
    </ClipModal>
  );
};
